import { isEmpty } from 'lodash';
import { useUpdateEffect } from 'react-use';

import {
   HealthcareFacilityUnitSimple,
   HealthcareFacilityUnitSimpleQuery,
} from '@dreryk/egabinet-healthcare-provider-frontend-api';

import { useCqrsSearch } from '@dreryk/command-query-react-common';
import { Message, MessageType, Tree } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { healthcareOrganizationTranslationKeys } from '../../../../translations';
import { HealthcareBasicData } from '../../../common/HealthcareBasicData';

type HealthcareFacilityUnitTreeItemsLoaderRenderProps = {
   healthcareFacilityUnits: HealthcareFacilityUnitSimple[];
   refetchHealthcareFacilityUnits: VoidFunction;
};

type HealthcareFacilityUnitTreeItemsLoaderProps = {
   readonly healthcareParentData: HealthcareBasicData;
   readonly children: (renderProps: HealthcareFacilityUnitTreeItemsLoaderRenderProps) => JSX.Element;
};

export const HealthcareFacilityUnitTreeItemsLoader = (
   props: HealthcareFacilityUnitTreeItemsLoaderProps
): JSX.Element => {
   const {
      data: healthcareFacilityUnits,
      loading: healthcareFacilityUnitsLoading,
      error: healthcareFacilityUnitsError,
      refetch: refetchHealthcareFacilityUnits,
   } = useCqrsSearch<HealthcareFacilityUnitSimple>(() => {
      const query = new HealthcareFacilityUnitSimpleQuery();
      query.healthcareFacilityId = Slot.of(props.healthcareParentData.id);
      return query;
   });

   useUpdateEffect(() => {
      refetchHealthcareFacilityUnits();
   }, [props.healthcareParentData]);

   if (healthcareFacilityUnitsLoading) {
      return <Tree.Loader testId={'loader'} />;
   }

   if (healthcareFacilityUnitsError) {
      return (
         <Message
            testId={'failedToFetchHealthcareFacilityUnit'}
            label={healthcareOrganizationTranslationKeys.failedToFetchHealthcareFacilityUnit}
            type={MessageType.NEGATIVE}
            displayIndicator
         />
      );
   }

   if (isEmpty(healthcareFacilityUnits)) {
      return (
         <Tree.Placeholder
            testId={'noData'}
            label={healthcareOrganizationTranslationKeys.noAssignHealthcareFacilityUnit}
         />
      );
   }

   return props.children({
      healthcareFacilityUnits: healthcareFacilityUnits,
      refetchHealthcareFacilityUnits: refetchHealthcareFacilityUnits,
   });
};
